import { login } from '../api'; // 確保 api.js 改咗 endpoint 為 `/Login/${guid}`
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../contexts/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/Login.css';
import { IconButton, Menu, MenuItem } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';

function Login() {
    const { updateCarParkInfo, clearUserData, currentLang, setCurrentLang } = useContext(AppContext);
    const navigate = useNavigate();
    const { guid } = useParams(); // 從 path 攞 GUID
    const [loginStatus, setLoginStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const languages = [
        { code: 'zh-tw', label: '繁' },
        { code: 'zh-cn', label: '简' },
        { code: 'en', label: '' }
    ];

    const getCurrentLangLabel = () => languages.find(l => l.code === currentLang)?.label || '';

    const getOtherLanguages = () => languages.filter(l => l.code !== currentLang);

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const handleClose = () => setAnchorEl(null);

    const handleLangChange = (langCode) => {
        setCurrentLang(langCode);
        handleClose();
    };

    const translations = {
        'zh-tw': {
            loadingTitle: '登入中',
            verifying: '正在驗證GUID...',
            successTitle: '登入成功',
            redirecting: '正在跳轉到查詢頁面...',
            errorTitle: '登入失敗',
            redirectingLogout: '正在跳轉到登出頁面...',
            noGuid: 'URL 無 GUID，請檢查路徑。',
            timeout: '登入超時 - 請檢查網絡',
            invalid: '無效的伺服器回應 - 缺少 carParkCode',
            defaultError: '登入失敗，請重試。'
        },
        'zh-cn': {
            loadingTitle: '登录中',
            verifying: '正在验证GUID...',
            successTitle: '登录成功',
            redirecting: '正在跳转到查询页面...',
            errorTitle: '登录失败',
            redirectingLogout: '正在跳转到登出页面...',
            noGuid: 'URL 无 GUID，请检查路径。',
            timeout: '登录超时 - 请检查网络',
            invalid: '无效的服务器响应 - 缺少 carParkCode',
            defaultError: '登录失败，请重试。'
        },
        'en': {
            loadingTitle: 'Logging in',
            verifying: 'Verifying GUID...',
            successTitle: 'Login Successful',
            redirecting: 'Redirecting to Enquiry...',
            errorTitle: 'Login Failed',
            redirectingLogout: 'Redirecting to logout...',
            noGuid: 'No GUID in URL path.',
            timeout: 'Login timeout - check network',
            invalid: 'Invalid server response - missing carParkCode',
            defaultError: 'Login failed, please try again.'
        }
    };

    const getTranslation = (key) => translations[currentLang][key] || translations['en'][key];

    const renderLangSelector = () => (
        <div style={{ 
            position: 'absolute', 
            top: '50px', 
            fontSize: '36px',
            right: '30px', 
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center'
        }}>
            <IconButton 
                onClick={handleClick}
                style={{
                    color: '#4a4a4aff', // Black globe as in image
                    padding: 0,
                }}
            >
                <LanguageIcon />
            </IconButton>
            <span style={{ marginLeft: '3px', color: '#4a4a4aff', fontSize: '30px' }}>
                {getCurrentLangLabel()}
            </span>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    },
                }}
            >
                {getOtherLanguages().map((lang) => (
                    <MenuItem 
                        key={lang.code} 
                        onClick={() => handleLangChange(lang.code)}
                        style={{
                            fontSize: '14px',
                            color: '#666',
                        }}
                    >
                        {lang.label}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );

    useEffect(() => {
        console.log('=== Login Debug Start ===');
        console.log('GUID from URL params:', guid);
        console.log('Current URL:', window.location.href);
        console.log('Current Language:', currentLang);
        
        if (guid) {
            setLoginStatus('loading'); 
            console.log('Calling login API with GUID:', guid);
            
            const loginPromise = login(guid);
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Login timeout - check network')), 15000)
            );
            
            Promise.race([loginPromise, timeoutPromise])
                .then(response => {
                    console.log('=== API Response ===', response);
                    if (response && response.carParkCode) {
                        console.log('Login SUCCESS! Data:', response);
                        setLoginStatus('success');
                        
                        const carParkData = {
                            carParkCode: response.carParkCode,
                            carParkNameEn: response.carParkNameEn,
                            carParkNameCn: response.carParkNameCn,
                            carParkNameTw: response.carParkNameTw,
                            guid: response.guid
                        };
                        console.log('Updating context:', carParkData);
                        updateCarParkInfo(carParkData);

                        // 關鍵改動：登入成功直接去 /enquiry
                        setTimeout(() => {
                            console.log('Navigating to /enquiry');
                            navigate('/enquiry');   // ← 改咗呢度！
                        }, 1000);
                    } else {
                        console.error('Invalid response structure:', response);
                        throw new Error('Invalid server response - missing carParkCode');
                    }
                })
                .catch(error => {
                    console.error('=== Login ERROR ===', error);
                    setLoginStatus('error');
                    
                    let errMsg = error.message === 'Login timeout - check network' ? getTranslation('timeout') :
                                error.message === 'Invalid server response - missing carParkCode' ? getTranslation('invalid') :
                                error.response?.data?.message || error.response?.data || error.message || getTranslation('defaultError');
                    
                    setErrorMessage(errMsg);
                    clearUserData();
                    setTimeout(() => {
                        console.log('Navigating to /logout');
                        navigate('/logout');
                    }, 3000);
                });
        } else {
            console.error('No GUID in path params!');
            setLoginStatus('error');
            setErrorMessage(getTranslation('noGuid'));
            clearUserData();
            setTimeout(() => navigate('/logout'), 2000);
        }
        
        console.log('=== Login Debug End ===');
    }, [guid, navigate, updateCarParkInfo, clearUserData, currentLang]);

    const renderLoadingState = () => (
        <div className="login-page" style={{ position: 'relative' }}>
            {renderLangSelector()}
            <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="login-logo" />
            <div className="login-container" style={{ paddingTop: '60px' }}>
                <h1 className="login-title">{getTranslation('loadingTitle')}</h1>
                <div className="login-spinner"></div>
                <p className="login-message">{getTranslation('verifying')}</p>
                <div className="login-dots">
                    <div></div><div></div><div></div><div></div>
                </div>
            </div>
        </div>
    );

    const renderSuccessState = () => (
        <div className="login-page" style={{ position: 'relative' }}>
            {renderLangSelector()}
            <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="login-logo" />
            <div className="login-container" style={{ paddingTop: '60px' }}>
                <h1 className="login-title">{getTranslation('successTitle')}</h1>
                <div className="login-success">{getTranslation('redirecting')}</div>
            </div>
        </div>
    );

    const renderErrorState = () => (
        <div className="login-page" style={{ position: 'relative' }}>
            {renderLangSelector()}
            <img src="/wilsonparking.jpg" alt="Wilson Parking Logo" className="login-logo" />
            <div className="login-container" style={{ paddingTop: '60px' }}>
                <h1 className="login-title">{getTranslation('errorTitle')}</h1>
                <div className="login-error">{errorMessage}</div>
                <p className="login-message">{getTranslation('redirectingLogout')}</p>
            </div>
        </div>
    );

    switch (loginStatus) {
        case 'success': return renderSuccessState();
        case 'error':   return renderErrorState();
        default:        return renderLoadingState();
    }
}

export default Login;